import axios from 'axios'

import { apiSlice } from '@/shared/api/apiSlice'
import { PERSONAL_API } from '@/shared/lib/utils/constants/const'

import { MAX_REVIEW_PHOTOS } from '../model/constants'
import { alignReviewsMediaOrder } from '../model/review-state'
import {
	CreateProductReviewInput,
	GetProductReviewMediaParams,
	GetProductReviewsParams,
	ProductReview,
	ProductReviewMediaResponse,
	ProductReviewRatings,
	ProductReviewsSummary,
	UpdateProductReviewInput,
	UserReviewsResponse,
	VoteForProductReviewInput
} from '../model/types'

import {
	ApiRecord,
	asNumber,
	asRecord,
	getPayloadMeta,
	getReviewCardItems,
	normalizeProductRatings,
	normalizeProductReviewsError,
	normalizeReviewCard,
	normalizeReviewMediaItems
} from './mappers'

const getReviewsUrl = (path: string) => `${PERSONAL_API}/v1/${path}`
const getFilterParams = (filter: ApiRecord) => ({ filter: JSON.stringify(filter) })
const getAuthHeaders = (token?: string) => (token ? { Authorization: `Bearer ${token}` } : undefined)
const getMultipartHeaders = (token?: string) => ({ Accept: 'application/json', ...(getAuthHeaders(token) ?? {}) })

export const productReviewSchemaPreviewQueryKey = (productCode: string | number) =>
	['product-review-schema-preview', String(productCode)] as const

const appendReviewFormData = (
	formData: FormData,
	input: Omit<CreateProductReviewInput, 'token'> & Pick<UpdateProductReviewInput, 'clearMedia'>,
	options: { includeProductName?: boolean } = {}
) => {
	formData.append('code', String(input.productCode))
	if (options.includeProductName && input.productName) {
		formData.append('product_name', input.productName)
	}
	formData.append('content', input.comment)
	formData.append('rating', String(input.rating))
	formData.append('is_anonymous', input.isAnonymous ? '1' : '0')
	const photos = input.attachments.filter(file => file.type.startsWith('image/')).slice(0, MAX_REVIEW_PHOTOS)
	photos.forEach((file, index) => formData.append('photos[' + index + ']', file))
	const video = input.attachments.find(file => file.type.startsWith('video/'))
	if (video) formData.append('video', video)
	input.clearMedia?.forEach((uuid, index) => formData.append('clear_media[' + index + ']', uuid))
}

const getReviewProductCodes = (params: Pick<GetProductReviewsParams, 'productCodes' | 'productCode' | 'variant'>) =>
	params.variant === 'all' ? params.productCodes : [params.productCode]

const getReviewSortProperty = (sort: GetProductReviewsParams['sort']) =>
	sort === 'newest' ? 'created_at' : sort === 'helpful' ? 'likes' : 'rating'

const getBaseReviewFilter = (
	params: Pick<
		GetProductReviewsParams,
		| 'productCodes'
		| 'productCode'
		| 'variant'
		| 'currentPage'
		| 'isPhoto'
		| 'isVideo'
		| 'rating'
		| 'reviewId'
		| 'ratingOrder'
		| 'sort'
	> & { perPage: number }
) => {
	const productCodes = getReviewProductCodes(params)
	const baseFilter = {
		code: productCodes.map(String),
		per_page: params.perPage,
		current_page: params.currentPage,
		is_photo: params.isPhoto ?? false,
		is_video: params.isVideo ?? false,
		...(params.reviewId ? { review_id: params.reviewId } : {}),
		sort: { property: getReviewSortProperty(params.sort), order: params.ratingOrder ?? 'desc' }
	}

	return params.rating?.length ? { ...baseFilter, rating: params.rating } : baseFilter
}

const getCardFilter = (params: GetProductReviewsParams) => getBaseReviewFilter({ ...params, perPage: params.limit })

const getMediaFilter = (params: GetProductReviewMediaParams) =>
	getBaseReviewFilter({ ...params, perPage: params.mediaLimit ?? 10 })

const getCrossLinkTotalCount = (payload: unknown) => {
	const meta = getPayloadMeta(payload)
	const totalCount = Number(meta.cross_link_total_count)

	return Number.isFinite(totalCount) ? totalCount : undefined
}

const transformProductReviewMedia = (payload: unknown): ProductReviewMediaResponse => {
	const items = normalizeReviewMediaItems(payload)
	const { mediaItems } = alignReviewsMediaOrder([], items)
	const pagination = getPayloadMeta(payload)
	const currentPage = asNumber(pagination.current_page)
	const lastPage = asNumber(pagination.last_page)

	return {
		items: mediaItems,
		total: asNumber(pagination.total) || mediaItems.length,
		hasMore: currentPage < lastPage,
		currentPage
	}
}

const transformProductReviewsSummary = (payload: unknown): ProductReviewsSummary => {
	const summaryData = asRecord(asRecord(payload).data)
	const distribution = [5, 4, 3, 2, 1].map(rating => ({
		rating,
		count: asNumber(summaryData[`rating_${rating}`])
	}))
	const total = distribution.reduce((result, item) => result + item.count, 0)
	const average = total ? distribution.reduce((result, item) => result + item.rating * item.count, 0) / total : 0

	return { average, total, distribution, criteria: [] }
}

const transformProductReviews = (
	cardsPayload: unknown,
	summaryPayload: unknown,
	mediaPayload: unknown,
	params: GetProductReviewsParams
) => {
	const reviews = getReviewCardItems(cardsPayload).map(normalizeReviewCard)
	const shouldIncludeMedia = params.includeMedia !== false
	const mediaItems = shouldIncludeMedia ? normalizeReviewMediaItems(mediaPayload) : []
	const { reviews: orderedReviews, mediaItems: orderedReviewMediaItems } = alignReviewsMediaOrder(reviews, mediaItems)
	const mediaPagination = getPayloadMeta(mediaPayload)
	const mediaTotal = asNumber(mediaPagination.total) || orderedReviewMediaItems.length
	const pagination = getPayloadMeta(cardsPayload)
	const currentPage = asNumber(pagination.current_page)
	const lastPage = asNumber(pagination.last_page)
	const totalCount = getCrossLinkTotalCount(cardsPayload)
	const baseSummary = params.cachedSummary ?? transformProductReviewsSummary(summaryPayload)
	const summary = totalCount === undefined ? baseSummary : { ...baseSummary, total: totalCount }

	return {
		summary,
		totalCount,
		variants: [
			{
				id: 'all' as const,
				name: 'Все модели',
				image: params.productImages?.[0],
				images: params.productImages?.slice(0, 2)
			},
			{
				id: 'current' as const,
				name: 'Этот вариант',
				image: params.productImages?.[0],
				images: params.productImages?.slice(0, 1)
			}
		],
		gallery: shouldIncludeMedia ? orderedReviewMediaItems.map(item => item.src) : [],
		...(shouldIncludeMedia ? { mediaItems: orderedReviewMediaItems, mediaTotal } : {}),
		reviews: orderedReviews,
		hasMore: lastPage ? currentPage < lastPage : reviews.length >= params.limit
	}
}

type GetProductReviewsPreviewParams = Pick<GetProductReviewsParams, 'productCode'> &
	Partial<Omit<GetProductReviewsParams, 'currentPage' | 'productCode'>>

export const fetchProductReviewsPreview = async (
	params: GetProductReviewsPreviewParams
): Promise<ReturnType<typeof transformProductReviews>> => {
	const currentPage = 1
	const requestParams: GetProductReviewsParams = {
		...params,
		sort: params.sort ?? 'newest',
		variant: params.variant ?? 'current',
		limit: params.limit ?? 5,
		productCodes: params.productCodes ?? [params.productCode],
		currentPage,
		isPhoto: params.isPhoto ?? false,
		isVideo: params.isVideo ?? false,
		includeSummary: params.includeSummary ?? false,
		includeMedia: params.includeMedia ?? false
	}
	const cardFilter = getCardFilter(requestParams)
	const response = await axios.get(getReviewsUrl('reviews/card'), {
		params: { ...getFilterParams(cardFilter), page: currentPage },
		headers: getAuthHeaders(params.token)
	})

	return JSON.parse(JSON.stringify(transformProductReviews(response.data, undefined, undefined, requestParams)))
}

export const productReviewsApi = apiSlice.injectEndpoints({
	endpoints: build => ({
		getProductReviewRatings: build.query<ProductReviewRatings, number[]>({
			query: productCodes => ({
				url: getReviewsUrl('reviews'),
				params: getFilterParams({ param: { code: productCodes } })
			}),
			transformResponse: normalizeProductRatings,
			keepUnusedDataFor: 60
		}),
		getProductReviewSummary: build.query<ProductReviewsSummary, number[]>({
			query: productCodes => ({
				url: getReviewsUrl('reviews/card/summary'),
				params: getFilterParams({ code: productCodes })
			}),
			transformResponse: transformProductReviewsSummary,
			keepUnusedDataFor: 60
		}),
		getProductReviews: build.query<ReturnType<typeof transformProductReviews>, GetProductReviewsParams>({
			async queryFn(params, _api, _extraOptions, baseQuery) {
				const cardFilter = getCardFilter(params)
				const mediaFilter = getMediaFilter({ ...params, currentPage: 1 })
				const [cardsResponse, summaryResponse, mediaResponse] = await Promise.all([
					baseQuery({
						url: getReviewsUrl('reviews/card'),
						params: { ...getFilterParams(cardFilter), page: params.currentPage },
						headers: getAuthHeaders(params.token)
					}),
					params.cachedSummary || params.includeSummary === false
						? Promise.resolve({ data: undefined })
						: baseQuery({
								url: getReviewsUrl('reviews/card/summary'),
								params: getFilterParams({ code: params.productCodes })
						  }),
					params.includeMedia === false
						? Promise.resolve({ data: undefined })
						: baseQuery({
								url: getReviewsUrl('reviews/card/media'),
								params: getFilterParams(mediaFilter),
								headers: getAuthHeaders(params.token)
						  })
				])
				if (cardsResponse.error) return { error: cardsResponse.error }
				if ('error' in summaryResponse && summaryResponse.error) return { error: summaryResponse.error }
				if ('error' in mediaResponse && mediaResponse.error) return { error: mediaResponse.error }
				return { data: transformProductReviews(cardsResponse.data, summaryResponse.data, mediaResponse.data, params) }
			},
			keepUnusedDataFor: 60
		}),
		getProductReviewMedia: build.query<ProductReviewMediaResponse, GetProductReviewMediaParams>({
			query: params => ({
				url: getReviewsUrl('reviews/card/media'),
				params: getFilterParams(getMediaFilter(params)),
				headers: getAuthHeaders(params.token)
			}),
			transformResponse: transformProductReviewMedia,
			keepUnusedDataFor: 60
		}),
		getUserReviews: build.query<UserReviewsResponse, { perPage: number; currentPage: number }>({
			query: ({ perPage, currentPage }) => ({
				url: getReviewsUrl('user/reviews'),
				params: {
					...getFilterParams({
						per_page: perPage,
						current_page: currentPage,
						sort: { property: 'created_at', order: 'desc' }
					}),
					page: currentPage
				}
			}),
			transformResponse: (payload: unknown) => {
				const meta = getPayloadMeta(payload)
				const reviews = getReviewCardItems(payload).map(item => {
					const review = normalizeReviewCard(item)
					return {
						...review,
						shop: review.shop ?? 'samsung'
					}
				})
				return {
					reviews,
					hasMore: asNumber(meta.current_page) < asNumber(meta.last_page)
				}
			},
			keepUnusedDataFor: 60
		}),
		getProductReviewById: build.query<ProductReview, string>({
			query: reviewId => ({
				url: getReviewsUrl(`reviews/show/${reviewId}`)
			}),
			transformResponse: (payload: unknown) => normalizeReviewCard(asRecord(asRecord(payload).data ?? payload)),
			keepUnusedDataFor: 60
		}),
		createProductReview: build.mutation<ProductReview, CreateProductReviewInput>({
			async queryFn({ token, ...input }) {
				try {
					const formData = new FormData()
					appendReviewFormData(formData, input, { includeProductName: true })
					const response = await axios.post(getReviewsUrl('reviews'), formData, {
						headers: getMultipartHeaders(token)
					})
					return { data: normalizeReviewCard(asRecord(asRecord(response.data).data ?? response.data)) }
				} catch (error) {
					const normalizedError = normalizeProductReviewsError(error)
					return {
						error: {
							status: 'CUSTOM_ERROR' as const,
							error: normalizedError.message,
							data: normalizedError
						}
					}
				}
			}
		}),
		updateProductReview: build.mutation<ProductReview, UpdateProductReviewInput>({
			async queryFn({ reviewId, token, ...input }) {
				try {
					const formData = new FormData()
					appendReviewFormData(formData, input)
					const response = await axios.post(getReviewsUrl('user/reviews/' + reviewId), formData, {
						headers: getMultipartHeaders(token)
					})
					return { data: normalizeReviewCard(asRecord(asRecord(response.data).data ?? response.data)) }
				} catch (error) {
					const normalizedError = normalizeProductReviewsError(error)
					return {
						error: {
							status: 'CUSTOM_ERROR' as const,
							error: normalizedError.message,
							data: normalizedError
						}
					}
				}
			}
		}),
		voteForProductReview: build.mutation<ProductReview | undefined, VoteForProductReviewInput>({
			query: ({ reviewId, vote }) => ({
				url: getReviewsUrl('reviews/reaction'),
				method: 'POST',
				params: { type: vote, reviews_id: reviewId }
			}),
			transformResponse: (payload: unknown) => {
				const root = asRecord(payload)
				const review = asRecord(root.data ?? payload)
				return Object.keys(review).length ? normalizeReviewCard(review) : undefined
			}
		})
	}),
	overrideExisting: false
})

export const {
	useGetProductReviewsQuery,
	useGetUserReviewsQuery,
	useLazyGetProductReviewByIdQuery,
	useLazyGetProductReviewMediaQuery,
	useLazyGetProductReviewRatingsQuery,
	useGetProductReviewSummaryQuery,
	useLazyGetProductReviewSummaryQuery,
	useLazyGetProductReviewsQuery,
	useCreateProductReviewMutation,
	useUpdateProductReviewMutation,
	useVoteForProductReviewMutation
} = productReviewsApi
